import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Upload, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { edgeTunnelApi } from '@/lib/api'
import { toast } from 'react-hot-toast'

interface OverrideImportModalProps {
  group: any
  onClose: () => void
  onSuccess: () => void
}

export default function OverrideImportModal({ group, onClose, onSuccess }: OverrideImportModalProps) {
  const [ipList, setIpList] = useState('')
  const [userLevel, setUserLevel] = useState(1)
  const queryClient = useQueryClient()

  const overrideImportMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.overrideImportNodes(data),
    onSuccess: (response: any) => {
      toast.success(response.data?.message || '覆盖导入成功')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '覆盖导入失败')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!ipList.trim()) {
      toast.error('请输入IP列表')
      return
    }

    const ips = ipList.trim().split('\n').filter(ip => ip.trim())
    if (ips.length === 0) {
      toast.error('请输入有效的IP地址')
      return
    }

    if (ips.length > 1000) {
      toast.error('单次最多导入1000个IP地址')
      return
    }

    overrideImportMutation.mutate({
      group_id: group.id,
      ip_list: ipList.trim(),
      user_level: userLevel
    })
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 1024 * 1024) { // 1MB limit
      toast.error('文件大小不能超过1MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      setIpList(content)
    }
    reader.readAsText(file)
  }

  const ipCount = ipList.trim() ? ipList.trim().split('\n').filter(ip => ip.trim()).length : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" />
              覆盖导入节点
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-sm text-gray-500">
            服务组: {group.name}
          </div>
        </CardHeader>

        <CardContent>
          {/* 警告提示 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">重要提示</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• 覆盖导入将删除该服务组的所有现有节点</li>
                  <li>• 然后导入新的IP列表作为节点</li>
                  <li>• 此操作不可撤销，请谨慎操作</li>
                  <li>• 建议先备份现有节点数据</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 文件上传 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                上传IP文件 (可选)
              </label>
              <input
                type="file"
                accept=".txt,.csv"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                支持 .txt 和 .csv 文件，每行一个IP地址
              </p>
            </div>

            {/* IP列表输入 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IP地址列表 *
              </label>
              <textarea
                value={ipList}
                onChange={(e) => setIpList(e.target.value)}
                className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="每行输入一个IP地址，例如：&#10;192.168.1.1&#10;192.168.1.2&#10;192.168.1.3"
                required
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>每行一个IP地址</span>
                <span>{ipCount} 个IP地址</span>
              </div>
            </div>

            {/* 用户等级 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                用户等级
              </label>
              <select
                value={userLevel}
                onChange={(e) => setUserLevel(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>所有用户</option>
                <option value={2}>标准用户</option>
                <option value={3}>VIP专享</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                设置这些节点的访问权限等级
              </p>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={overrideImportMutation.isPending || ipCount === 0}
                className="flex-1 flex items-center gap-2 bg-red-600 hover:bg-red-700"
              >
                <Upload className="h-4 w-4" />
                {overrideImportMutation.isPending ? '导入中...' : `覆盖导入 ${ipCount} 个节点`}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}